/**
 * TanStack Query のクエリキー定義
 *
 * 命名規則:
 * - all: そのドメインの全キャッシュを invalidate するときに使用
 * - list: 一覧取得用
 * - detail: 詳細取得用（ID をキーに含める）
 */
export const queryKeys = {
  // 認証
  auth: {
    all: ['auth'] as const,
    check: () => [...queryKeys.auth.all, 'check'] as const,
  },

  // 患者関連
  patients: {
    all: ['patients'] as const,
    list: () => [...queryKeys.patients.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.patients.all, 'detail', id] as const,
  },

  // 薬歴関連
  yakureki: {
    all: ['yakureki'] as const,
    detail: (id: string) => [...queryKeys.yakureki.all, id] as const,
    delta: (lastUpdated: string) =>
      [...queryKeys.yakureki.all, 'delta', lastUpdated] as const,
  },

  // 指導支援
  guidance: {
    all: ['guidance'] as const,
    detail: (yakurekiId: string) =>
      [...queryKeys.guidance.all, yakurekiId] as const,
  },

  // 患者の属性 (指導前準備の監査の面)
  patientAttributes: {
    all: ['patientAttributes'] as const,
    byEvent: (yakurekiId: string) =>
      [...queryKeys.patientAttributes.all, yakurekiId] as const,
  },

  // 医薬品グループ
  drugGroups: {
    all: ['drugGroups'] as const,
    list: (tenantId: string) =>
      [...queryKeys.drugGroups.all, tenantId] as const,
    detail: (groupId: string) =>
      [...queryKeys.drugGroups.all, 'detail', groupId] as const,
    drugs: (groupId: string) =>
      [...queryKeys.drugGroups.all, groupId, 'drugs'] as const,
    soaps: (groupId: string) =>
      [...queryKeys.drugGroups.all, groupId, 'soaps'] as const,
  },

  // 医薬品
  drugs: {
    all: ['drugs'] as const,
    list: (params?: { page?: number; search?: string }) =>
      [...queryKeys.drugs.all, 'list', params] as const,
    detail: (drugId: string) =>
      [...queryKeys.drugs.all, 'detail', drugId] as const,
    soaps: (drugId: string) =>
      [...queryKeys.drugs.all, drugId, 'soaps'] as const,
    search: (params: {
      keyword?: string
      conditions?: unknown[]
      page?: number
      isAdvancedMode?: boolean
    }) => [...queryKeys.drugs.all, 'search', params] as const,
  },

  // SOAP テンプレート
  soapTemplates: {
    all: ['soapTemplates'] as const,
    list: (tenantId: string) =>
      [...queryKeys.soapTemplates.all, tenantId] as const,
  },

  // SOAP 改善提案
  soapImprovement: {
    all: ['soapImprovement'] as const,
    detail: (yakurekiId: string) =>
      [...queryKeys.soapImprovement.all, yakurekiId] as const,
  },

  // メトリクス
  // Note: params はリクエストパラメータ全体を含める（@tanstack/query/exhaustive-deps 対応）
  metrics: {
    all: ['metrics'] as const,
    summary: <T extends object>(params: T) =>
      [...queryKeys.metrics.all, 'summary', params] as const,
    analytics: <T extends object>(params: T) =>
      [...queryKeys.metrics.all, 'analytics', params] as const,
    records: <T extends object>(params: T) =>
      [...queryKeys.metrics.all, 'records', params] as const,
    detail: (id: string) => [...queryKeys.metrics.all, 'detail', id] as const,
    comparison: <T extends object>(params: T) =>
      [...queryKeys.metrics.all, 'comparison', params] as const,
  },

  // 設定
  setting: {
    all: ['setting'] as const,
  },

  // テキスト置換
  textReplacements: {
    all: ['textReplacements'] as const,
  },

  // フォーマット挿入ルール
  formatInsertRules: {
    all: ['formatInsertRules'] as const,
  },

  // SOAPルール (v3)
  soapRules: {
    all: ['soapRules'] as const,
  },

  // トレーシングレポート (提出ルール + セッション別文書)
  tracingReport: {
    rules: ['tracingReport', 'rules'] as const,
    items: (yakurekiId: string) =>
      ['tracingReport', 'items', yakurekiId] as const,
  },

  // 定型文 (SOAPセット。薬歴の挿入ダイアログと 設定 > 定型文 が共有する)
  soapMasters: {
    all: ['soapMasters'] as const,
    list: () => [...queryKeys.soapMasters.all, 'list'] as const,
  },

  // チェックポイント
  checkpoints: {
    all: ['checkpoints'] as const,
    detail: (requestId: string) =>
      [...queryKeys.checkpoints.all, requestId] as const,
  },

  // 患者管理
  patientManagement: {
    all: ['patientManagement'] as const,
    patients: (searchParams?: Record<string, unknown>) =>
      [...queryKeys.patientManagement.all, 'patients', searchParams] as const,
    patientDetail: (patientId: string) =>
      [...queryKeys.patientManagement.all, 'patientDetail', patientId] as const,
    patientDetails: (patientId: string) =>
      [
        ...queryKeys.patientManagement.all,
        'patientDetails',
        patientId,
      ] as const,
    patientVisits: (patientId: string) =>
      [...queryKeys.patientManagement.all, 'patientVisits', patientId] as const,
  },

  // 患者アクション
  patientActions: {
    all: ['patientActions'] as const,
    forPatient: (patientId: string) =>
      [...queryKeys.patientActions.all, 'forPatient', patientId] as const,
    list: () => [...queryKeys.patientActions.all, 'list'] as const,
    detail: (actionId: string) =>
      [...queryKeys.patientActions.all, 'detail', actionId] as const,
  },

  // 患者メッセージング（LINE連携）
  patientMessaging: {
    all: ['patientMessaging'] as const,
    threads: () => [...queryKeys.patientMessaging.all, 'threads'] as const,
    thread: (threadId: string) =>
      [...queryKeys.patientMessaging.all, 'thread', threadId] as const,
    lineStatus: (patientId: string) =>
      [...queryKeys.patientMessaging.all, 'lineStatus', patientId] as const,
    lineSettings: () =>
      [...queryKeys.patientMessaging.all, 'lineSettings'] as const,
  },

  // チャット
  chat: {
    all: ['chat'] as const,
    history: (yakurekiId: string) =>
      [...queryKeys.chat.all, yakurekiId] as const,
  },

  // 薬局
  pharmacy: {
    all: ['pharmacy'] as const,
    list: () => [...queryKeys.pharmacy.all, 'list'] as const,
    pharmacists: () => [...queryKeys.pharmacy.all, 'pharmacists'] as const,
    me: () => [...queryKeys.pharmacy.all, 'me'] as const,
  },

  // 加算履歴
  kasanHistory: {
    all: ['kasanHistory'] as const,
    detail: (yakurekiId: string) =>
      [...queryKeys.kasanHistory.all, yakurekiId] as const,
  },

  // 算定分析レポート
  kasanAnalysis: {
    all: ['kasanAnalysis'] as const,
    report: () => [...queryKeys.kasanAnalysis.all, 'report'] as const,
  },

  // 剤形オプション
  dosageFormOptions: {
    all: ['dosageFormOptions'] as const,
    list: () => [...queryKeys.dosageFormOptions.all, 'list'] as const,
  },

  // AI生成
  aiGeneration: {
    all: ['aiGeneration'] as const,
    usage: () => [...queryKeys.aiGeneration.all, 'usage'] as const,
  },

  // グループセッション
  recordingSessions: {
    all: ['recordingSessions'] as const,
    detail: (id: string) =>
      [...queryKeys.recordingSessions.all, 'detail', id] as const,
  },

  // SOAP編集履歴
  soapEditHistory: {
    all: ['soapEditHistory'] as const,
    list: (yakurekiId: string) =>
      [...queryKeys.soapEditHistory.all, yakurekiId] as const,
  },

  // 処方の読み (Kakeru Education)
  prescriptionReading: {
    all: ['prescriptionReading'] as const,
    detail: (yakurekiId: string) =>
      [...queryKeys.prescriptionReading.all, yakurekiId] as const,
  },

  // マニュアル照会 (社内マニュアル・行政通知への質問)
  knowledge: {
    all: ['knowledge'] as const,
    conversations: () => [...queryKeys.knowledge.all, 'conversations'] as const,
    conversation: (id: string) =>
      [...queryKeys.knowledge.all, 'conversation', id] as const,
  },
} as const
